import { ChartData } from "components/dashboard/dataset-window/models/types";
import { DatasetLoadingConf, IDataSet } from "components/dashboard/charts/interfaces";
import { addToDate, formatTime, truncateDate } from "utils/time-utils";

export function defaultFromDateTime(props: {
  loadConf?: DatasetLoadingConf;
  referenceTime?: string;
}) {
  const align = props.loadConf?.alignUnit;
  const lag = props.loadConf?.lag ?? 6;
  const lagUnit = props.loadConf?.lagUnit ?? "HOURS";
  const startingDate = props.referenceTime ? new Date(props.referenceTime) : new Date();
  const date = truncateDate(startingDate, align);

  return addToDate(date, -lag, lagUnit);
}

export function defaultToDateTime(props: {
  loadConf?: DatasetLoadingConf;
  referenceTime?: string;
}) {
  const align = props.loadConf?.alignUnit;
  const lead = props.loadConf?.lead ?? 0;
  const leadUnit = props.loadConf?.leadUnit ?? "HOURS";

  const startingDate = props.referenceTime ? new Date(props.referenceTime) : new Date();

  const date = truncateDate(startingDate, align);

  return addToDate(addToDate(date, lead, leadUnit), 1, "MINUTES");
}

export const convertStringDateToDate = (dataArray: Array<any>) => {
  return dataArray.map(d => {
    return new Date(d["key"]);
  });
};

export const convertValue = (dataArray: Array<any>) => {
  return dataArray?.map(d => {
    return d["value"];
  });
};

export const convertKey = (dataArray: Array<any>) => {
  return dataArray.map(d => {
    return new Date(d["key"]);
  });
};

export const convertDataToChartsData = (datasetArray: Array<IDataSet>): Array<ChartData> => {
  return datasetArray.map(d => {
    return {
      id: d.identifier,
      title: d.title.replace(/_/g, " "),
      unit: d.unit,
      keyArr: convertKey(d.data),
      valueArr: convertValue(d.data),
      decimalPlaces: d.decimalPlaces,
    };
  });
};

export const isElementInDom = (id: string) => {
  const elementExists = document.getElementById(id);
  return !!elementExists;
};

export const round2decimals = (num: number) => {
  return Math.round((num + Number.EPSILON) * 100) / 100;
};

export const hexToRgb = (hex: string) => {
  var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  let colorRGB = null;
  if (result !== null) {
    const r = parseInt(result[1], 16);
    const g = parseInt(result[2], 16);
    const b = parseInt(result[3], 16);
    colorRGB = `rgba(${r},${g},${b},0.7)`;
  }
  return colorRGB ? colorRGB : null;
};

interface ITableRowData {
  date: Date;

  [key: string]: string | Date | null;
}

export const getAllDatasetsTimestamps = (
  datasets: Array<IDataSet>
): {
  content: Array<any>;
  columns: Array<string>;
} => {
  const columns: string[] = [];
  const tableTemplate: { [key: string]: ITableRowData } = {};

  for (const dataset of datasets) {
    const columnName = `${dataset.title.replaceAll("_", " ")}${dataset.unit ? `, ${dataset.unit}` : ""}`;
    columns.push(columnName);

    for (const { key: dateStr, value } of dataset.data) {
      const date = new Date(dateStr);
      const tempKey = `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()} ${date.getHours()}:${date.getMinutes()}:${date.getSeconds()}`;

      tableTemplate[tempKey] = {
        ...(tableTemplate[tempKey] ?? {}),
        date: date,
        [columnName]: value != null ? value.toFixed(dataset.decimalPlaces ?? 2) : null,
      };
    }
  }

  const tableTemplateArray = Object.entries(tableTemplate)
    .map(([key, value]) => {
      return {
        timestamp: key,
        ...value,
      };
    })
    .sort((a, b) => {
      return a.date.getTime() - b.date.getTime();
    });

  return {
    columns,
    content: tableTemplateArray.map(obj => ({ ...obj, timestamp: formatTime(obj.timestamp) })),
  };
};

export const splitLongWord = (word: string) => {
  const chunks = [];
  for (let i = 0; i < word.length; i += 15) {
    chunks.push(word.substring(i, i + 15));
  }
  return chunks.join("-<br>");
};

export const splitStringToLines = (str: string) => {
  const words = str.split(" ");
  const lines = [];
  let currentLine = "";
  for (const word of words) {
    if (currentLine.length + word.length + 1 <= 15) {
      currentLine += (currentLine ? " " : "") + word;
    } else {
      lines.push(currentLine);
      currentLine = word;
    }
  }
  if (currentLine) {
    lines.push(currentLine);
  }
  return lines;
};
